import type { Camera } from '../engine/Camera';
import type { CelebrationSystem } from '../effects/Celebration';

/**
 * Renders the 4 celebration layers in their correct visual order:
 *
 *   1. Shockwave rings (under everything — fast, expanding)
 *   2. Flash (bright pulse — over rings, brief)
 *   3. Confetti (mid-air, longest lifetime)
 *   4. Sparkles (top — twinkly accents, eye-catchers)
 *
 * Easing helpers below mirror the cubic-bezier feel of Framer Motion /
 * Magic UI premium presets. Pure functions, zero allocations.
 */
export class CelebrationRenderer {
  draw(ctx: CanvasRenderingContext2D, camera: Camera, system: CelebrationSystem): void {
    const z = camera.zoom;
    const ox = camera.viewportW * 0.5 + camera.shakeX;
    const oy = camera.viewportH * 0.5 + camera.shakeY;
    const cax = camera.position.x;
    const cay = camera.position.y;

    const project = (wx: number, wy: number): [number, number] => [
      (wx - cax) * z + ox,
      (wy - cay) * z + oy,
    ];

    // ---- 1. Shockwave rings ----
    ctx.save();
    ctx.lineCap = 'round';
    system.forEachRing((r) => {
      // Easing: ease-out cubic. radius grows fast then settles.
      const t = 1 - r.life / r.maxLife;            // 0 → 1
      const eased = 1 - Math.pow(1 - t, 3);        // ease-out cubic
      const radius = r.maxRadius * eased * z;
      // Alpha: fades from 0.9 → 0 over life.
      const alpha = Math.max(0, r.life / r.maxLife);
      const [sx, sy] = project(r.x, r.y);
      ctx.strokeStyle = `hsla(${r.hue} 100% 72% / ${0.7 * alpha})`;
      ctx.shadowColor = `hsl(${r.hue} 100% 60%)`;
      ctx.shadowBlur = 8;
      ctx.lineWidth = 1.5 + 2.5 * alpha;
      ctx.beginPath();
      ctx.arc(sx, sy, radius, 0, Math.PI * 2);
      ctx.stroke();
    });
    ctx.restore();

    // ---- 2. Flash ----
    ctx.save();
    system.forEachFlash((f) => {
      const t = 1 - f.life / f.maxLife;
      const alpha = Math.max(0, 1 - t);
      const [sx, sy] = project(f.x, f.y);
      const radius = 50 * z;
      const g = ctx.createRadialGradient(sx, sy, 0, sx, sy, radius);
      g.addColorStop(0, `hsla(${f.hue} 100% 95% / ${0.8 * alpha})`);
      g.addColorStop(0.4, `hsla(${f.hue} 100% 65% / ${0.4 * alpha})`);
      g.addColorStop(1, `hsla(${f.hue} 100% 50% / 0)`);
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.arc(sx, sy, radius, 0, Math.PI * 2);
      ctx.fill();
    });
    ctx.restore();

    // ---- 3. Confetti ----
    ctx.save();
    system.forEachConfetto((c) => {
      const lifeT = c.life / c.maxLife;
      // Alpha: stays full for first 60%, then fades.
      const alpha = lifeT > 0.4 ? 1 : Math.max(0, lifeT / 0.4);
      const [sx, sy] = project(c.x, c.y);
      const size = c.size * z;
      ctx.save();
      ctx.translate(sx, sy);
      ctx.rotate(c.rot);
      ctx.globalAlpha = alpha;
      ctx.shadowColor = `hsl(${c.hue} 100% 65%)`;
      ctx.shadowBlur = 6;
      ctx.fillStyle = `hsl(${c.hue} 100% 65%)`;
      if (c.shape === 0) {
        // Square — premium shape, slightly rounded
        ctx.fillRect(-size, -size * 0.5, size * 2, size);
      } else if (c.shape === 1) {
        // Triangle
        ctx.beginPath();
        ctx.moveTo(0, -size);
        ctx.lineTo(size * 0.95, size * 0.7);
        ctx.lineTo(-size * 0.95, size * 0.7);
        ctx.closePath();
        ctx.fill();
      } else {
        // Circle
        ctx.beginPath();
        ctx.arc(0, 0, size * 0.75, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.restore();
    });
    ctx.restore();
    ctx.globalAlpha = 1;

    // ---- 4. Sparkles — 4-point cross + center dot, scale eased ----
    ctx.save();
    ctx.lineCap = 'round';
    system.forEachSparkle((s) => {
      // pop-in then pop-out scale curve: triangle-wave-ish via sin.
      const lifeT = s.life / s.maxLife;
      const t = 1 - lifeT;                                // 0 → 1
      // Eased scale: snappy peak at t=0.35, gentle fade out.
      const scale = t < 0.35
        ? CelebrationRenderer.easeOutBack(t / 0.35)
        : 1 - CelebrationRenderer.easeInCubic((t - 0.35) / 0.65);
      const alpha = Math.max(0, Math.min(1, scale));
      if (alpha <= 0.001) return;
      const [sx, sy] = project(s.x, s.y);
      const r = s.radius * z * scale;
      ctx.strokeStyle = `hsla(${s.hue} 100% 92% / ${alpha})`;
      ctx.shadowColor = `hsl(${s.hue} 100% 85%)`;
      ctx.shadowBlur = 10;
      ctx.lineWidth = Math.max(1.0, r * 0.12);
      // Long horizontal + vertical axes
      ctx.beginPath();
      ctx.moveTo(sx - r, sy); ctx.lineTo(sx + r, sy);
      ctx.moveTo(sx, sy - r); ctx.lineTo(sx, sy + r);
      ctx.stroke();
      // Center dot
      ctx.fillStyle = `hsla(0 0% 100% / ${alpha})`;
      ctx.beginPath();
      ctx.arc(sx, sy, Math.max(1, r * 0.16), 0, Math.PI * 2);
      ctx.fill();
    });
    ctx.restore();
  }

  // ---------------------------------------------------------------------------
  // Easing — direct ports of Framer Motion / Magic UI standard curves
  // ---------------------------------------------------------------------------

  /** Snappy overshoot pop-in (Framer's `backOut`). */
  private static easeOutBack(t: number): number {
    const c1 = 1.70158;
    const c3 = c1 + 1;
    return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
  }
  /** Soft ease-in (cubic). */
  private static easeInCubic(t: number): number {
    return t * t * t;
  }
}
